import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
  useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbarContext } from "../../component/SnackbarContext";
import { addData, getAllData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import { useDispatch, useSelector } from "react-redux";
import { setLoginData } from "../../redux/Reducers/AuthReducer/authSplice";
import TimerButton from "../BuyCoins/TimerButton";
import { setFreeSpinGames } from "../../redux/Reducers/GlobalReducer/globalSlice";
import BoosterClaim from "./BoosterClaim";
import ShortStreak from "./ShortStreak";
import StreakProgress from "./StreakProgress";
import StreakCelebration from "./StreakCelebration";
import StreakAtRisk from "./StreakAtRisk";
import PaymentModal from "../BuyCoins/PaymentModal";

const MonthlyPromotions = ({ open, onClose, rewardsData = [], userProgress = 1, fromHome = false, promotionName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarContext();
  const authState = useSelector((state) => state.auth);
  const { IsLoginData, isUserInAllowedRegion, isAuthenticated } = authState;

  // Get current day from API data
  const currentDay = IsLoginData?.dailyReward?.currentDay || 1;
  const [monthlyData, setMonthlyData] = useState([]);
  const dispatch = useDispatch();

  // Booster claim state
  const [showBoosterClaim, setShowBoosterClaim] = useState(false);
  const [boosterRewardData, setBoosterRewardData] = useState(null);

  const [startingNew, setStartingNew] = useState(false);

  // Streak-at-risk state
  const [riskBusy, setRiskBusy] = useState(false);
  const [recoveryPackage, setRecoveryPackage] = useState(null);
  const [recoveryInfo, setRecoveryInfo] = useState(null); // package details for display (price)

  useEffect(() => {
    if (open && IsLoginData?.idUser) {
      fetchMonthlyPromotions();
    }
  }, [open, IsLoginData?.dailyReward?.streakType]);

  // Load the recovery package details (for the price shown on the at-risk screen).
  useEffect(() => {
    const recoveryPackageId = IsLoginData?.dailyReward?.recoveryPackageId;
    if (open && IsLoginData?.dailyReward?.streakRisk === true && recoveryPackageId && !recoveryInfo) {
      (async () => {
        try {
          const response = await getAllData(endPoints.api.GET_PACKAGE);
          const pkg = response?.data?.find((p) => p?.id === recoveryPackageId);
          if (pkg) setRecoveryInfo(pkg);
        } catch (error) {
          console.error("Recovery package fetch error:", error);
        }
      })();
    }
  }, [open, IsLoginData?.dailyReward?.streakRisk, IsLoginData?.dailyReward?.recoveryPackageId]);

  const handleClose = () => {
    if (fromHome && IsLoginData?.idUser) {
      const userId = IsLoginData.idUser;
      const now = Date.now();

      localStorage.setItem(`monthlyPromoafterLogin_${userId}`, "true");
      localStorage.setItem(`monthlyPromoTimestamp_${userId}`, now.toString());
    }

    onClose();
  };

  const fetchMonthlyPromotions = async () => {
    if (!IsLoginData?.idUser) {
      console.warn("Cannot fetch monthly promotions: User ID not available");
      return;
    }
    try {
      const url = `${endPoints.api.MONTHLY_REWARDS}`;
      const response = await getAllData(url);
      if (response?.status == "success") {
        setMonthlyData(response?.data);
      } else {
        if (!fromHome) console.log("Failed to fetch monthly promotions data");
      }
    } catch (error) {
      console.error(error);
      if (!fromHome) console.log("Failed to fetch monthly promotions data.");
    }
  };

  // Helper function to format reward display
  const formatReward = (rewardType, rewardValue, boosterValue) => {
    switch (rewardType) {
      case 'CASH':
      case 'SAGA_COIN':
        return { text: `${rewardValue} ${IsLoginData?.currency || "USD"}`, icon: '🥇', color: '#ff6b35' };
      case 'BONUS':
      case 'GOLD_COIN':
        return { text: `${rewardValue} Bonus`, icon: '💎', color: '#ffd700' };
      case 'GIFTCARD':
        return { text: `$${rewardValue} ${t('monthlyPromotions.gift')}`, icon: '🎁', color: '#4caf50' };
      case 'BOOSTER':
        return { text: `${boosterValue ? boosterValue : ""}${t('monthlyPromotions.boostOnBuy')}`, icon: '🚀', color: '#ff9800' };
      case 'FREE_SPIN':
        return { text: `${rewardValue ? rewardValue : ""} ${t('monthlyPromotions.freeSpins')}`, icon: '🎰', color: '#9c27b0' };
      default:
        return { text: `+${rewardValue}`, icon: '💰', color: '#2196f3' };
    }
  };

  // Generate days based on API data and current progress
  const generateDays = (currentDay, apiData) => {
    if (!Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }
    return apiData.map((item) => {
      const dayNumber = item?.dayNumber;
      const reward = formatReward(item?.rewardType, item?.rewardValue, item?.booster);

      return {
        id: item.id,
        day: dayNumber,
        title: item?.title,
        status: dayNumber < currentDay ? "claimed" :
                dayNumber === currentDay ? "available" : "locked",
        reward: reward,
        rewardType: item?.rewardType,
        rewardValue: item?.rewardValue,
        isSpecial: item?.isSpecial,
        occasion: item?.occasion,
        multiplier: item?.multiplier,
        apiData: item
      };
    });
  };

  const days = generateDays(currentDay, monthlyData);

  // API function to claim daily reward
  const claimDailyReward = async (rewardId) => {
    // Streak at risk -> don't claim; the at-risk overlay handles recovery.
    if (IsLoginData?.dailyReward?.streakRisk === true) {
      showErrorSnackbar("Your streak is at risk! Please recover it to claim rewards.");
      return;
    }
    // Check if it's a booster reward that requires payment
    if (rewardId.rewardType === "BOOSTER") {
      setBoosterRewardData(rewardId);
      setShowBoosterClaim(true);
      return;
    }

    // Normal claim flow for other rewards
    const url = `${endPoints.api.CLAIM_MONTHLY_REWARD}`;
    const data = {
      rewardId: rewardId?.id,
      userId: IsLoginData?.idUser
    };
    try {
      const response = await addData(url, data);
      if (response?.data?.status == "success") {
        showSuccessSnackbar(response?.data?.message || t('monthlyPromotions.rewardClaimedSuccess'));
        const completed = response?.data?.data?.dailyReward?.streakCompleted === true;
        await GetUserById();
        fetchFreeSpinGames();
        // On the last day the streak completes -> keep modal open to show congrats.
        if (!completed) handleClose();
      } else {
        showErrorSnackbar(t('monthlyPromotions.failedToClaimRetry'));
        handleClose();
      }
    } catch (error) {
      console.error("Claim reward error:", error);
      showErrorSnackbar(t('monthlyPromotions.failedToClaim'));
    }
  };

  const fetchFreeSpinGames = async () => {
    try {
      const url = `${endPoints.api.RECENT_FREE_SPIN_GAMES}/${IsLoginData.idUser}`;
      const response = await getAllData(url);

      if (response?.status === "success" && response?.data) {
        dispatch(setFreeSpinGames(response.data));
      }
    } catch (error) {
      console.error("Error fetching free spin games:", error);
      dispatch(setFreeSpinGames([]));
    }
  };

  const GetUserById = async () => {
    try {
      if (IsLoginData?.idUser) {
        const url = `${endPoints.api.GET_BY_USERID}/${IsLoginData?.idUser}`;
        const response = await getAllData(url);
        dispatch(setLoginData({ user: response.data, isAuthenticated: true }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Start New Streak: resets user to day 1 (streakType 0) so they pick a plan again.
  const startNewStreak = async () => {
    if (!IsLoginData?.idUser) return;
    setStartingNew(true);
    try {
      const url = `${endPoints.api.NEW_STREAK}/${IsLoginData.idUser}`;
      const response = await addData(url);
      const status = (response?.data?.status || "").toString().toLowerCase();
      if (status === "success") {
        showSuccessSnackbar(response?.data?.message || "New streak started.");
        await GetUserById();
        handleClose();
      } else {
        showErrorSnackbar(response?.data?.message || "Failed to start a new streak.");
      }
    } catch (error) {
      console.error("Start new streak error:", error);
      showErrorSnackbar("Failed to start a new streak.");
    } finally {
      setStartingNew(false);
    }
  };

  // Streak at risk: start fresh -> reset to day 1.
  const startFreshStreak = async () => {
    if (!IsLoginData?.idUser) return;
    setRiskBusy(true);
    try {
      const url = `${endPoints.api.START_FRESH_STREAK}/${IsLoginData.idUser}`;
      const response = await addData(url);
      const status = (response?.data?.status || "").toString().toLowerCase();
      if (status === "success") {
        showSuccessSnackbar(response?.data?.message || "Started a fresh streak.");
        await GetUserById();
        handleClose();
      } else {
        showErrorSnackbar(response?.data?.message || "Failed to start fresh.");
      }
    } catch (error) {
      console.error("Start fresh streak error:", error);
      showErrorSnackbar("Failed to start fresh.");
    } finally {
      setRiskBusy(false);
    }
  };

  // Streak at risk: buy the recovery package to keep the streak.
  const buyToKeepStreak = async () => {
    const recoveryPackageId = IsLoginData?.dailyReward?.recoveryPackageId;
    if (!recoveryPackageId) {
      showErrorSnackbar("Recovery package unavailable right now.");
      return;
    }
    if (recoveryInfo) {
      setRecoveryPackage(recoveryInfo);
      handleClose();
      return;
    }
    setRiskBusy(true);
    try {
      const response = await getAllData(endPoints.api.GET_PACKAGE);
      const pkg = response?.data?.find((p) => p?.id === recoveryPackageId);
      if (pkg) {
        setRecoveryInfo(pkg);
        setRecoveryPackage(pkg);
        handleClose();
      } else {
        showErrorSnackbar("Recovery package not found.");
      }
    } catch (error) {
      console.error("Buy to keep streak error:", error);
      showErrorSnackbar("Could not open the buy flow. Please try again.");
    } finally {
      setRiskBusy(false);
    }
  };

  const claimedDays = IsLoginData?.dailyReward?.claimed ? currentDay : currentDay - 1;
  const totalDays = monthlyData.length;

  // Layout adapts to the number of days:
  //  - <= 10 days -> compact vertical timeline (ShortStreak component)
  //  - >  10 days -> compact grid (calendar design)
  const layout = totalDays <= 10 ? "short" : "calendar";

  return (
    (monthlyData?.length > 0 || IsLoginData?.dailyReward?.streakCompleted === true || IsLoginData?.dailyReward?.streakRisk === true) && (
    <>
      {/* Booster Claim Component */}
      <BoosterClaim
        open={showBoosterClaim}
        rewardData={boosterRewardData}
        onCloseMain={handleClose}
        onCloseBooster={() => {
          setShowBoosterClaim(false);
          setBoosterRewardData(null);
        }}
      />

    <Dialog
      open={open}
      maxWidth={layout === "short" ? "sm" : "lg"}
      fullWidth
      sx={{
        zIndex: 10101
      }}
      PaperProps={{
        sx: {
          background: "linear-gradient(135deg, #1a237e, #3949ab, #5c6bc0)",
          borderRadius: 3,
          position: "relative",
          overflow: "visible",
        }
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          background: "rgba(255, 255, 255, 0.2)",
          color: "#fff",
          zIndex: 10,
          "&:hover": {
            background: "rgba(255, 255, 255, 0.3)",
          }
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{
        p: 0,
        overflow: "auto",
        '&::-webkit-scrollbar': { width: '8px', background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.2)', borderRadius: '4px' }
      }}>
        <Box sx={{
          display: "flex",
          minHeight: "auto",
          flexDirection: isMobile ? "column" : "row"
        }}>
          {/* Left Section - Daily Login Calendar */}
          <Box sx={{
            flex: 1,
            p: isMobile ? 2 : 3,
            background: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(10px)",
            overflowY: "auto",
            '&::-webkit-scrollbar': { width: '6px', background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.15)', borderRadius: '3px' }
          }}>
            {/* Header */}
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                color: "#fff",
                fontWeight: "bold",
                mb: 1,
                textShadow: "2px 2px 4px rgba(0,0,0,0.5)"
              }}
            >
             {promotionName || t('monthlyPromotions.dailyPrizeRun')}
            </Typography>

            {IsLoginData?.dailyReward?.streakType > 0 && (
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#ffeb3b",
                  mb: 2,
                  background: "rgba(255, 235, 59, 0.1)",
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  display: "inline-block",
                  border: "1px solid rgba(255, 235, 59, 0.3)"
                }}
              >
                {totalDays} {t('monthlyPromotions.day')} Streak
              </Typography>
            )}

            {IsLoginData?.dailyReward?.claimed && <Typography
              variant="subtitle1"
              sx={{
                color: "#e3f2fd",
                mb: 3,
                background: "rgba(255, 255, 255, 0.1)",
                px: 2,
                py: 0.5,
                marginLeft: { xs: 0, md: 2 },
                borderRadius: 2,
                display: "inline-block"
              }}
            >
              {t('monthlyPromotions.nextReward')} {" "} <TimerButton
                                            initialTime={IsLoginData?.dailyReward?.timer}
                                            isDailyLoginClaim={IsLoginData?.dailyReward?.claimed}
                                          />
            </Typography>
            }

            {/* ---------- Streak progress bar ---------- */}
            <StreakProgress
              currentDay={currentDay}
              totalDays={totalDays}
              claimed={IsLoginData?.dailyReward?.claimed}
            />

            {/* Short streaks (<=10) -> compact vertical timeline */}
            {layout === "short" && (
              <ShortStreak
                days={days}
                currentDay={currentDay}
                claimed={IsLoginData?.dailyReward?.claimed}
                onClaim={claimDailyReward}
              />
            )}

            {/* Calendar grid for long streaks (>10) */}
            {layout === "calendar" && (
            <Grid container spacing={1}>
              {days?.map((dayData) => {
                const { day, status, reward, isSpecial } = dayData;
                return (
                <Grid item xs={4} sm={3} md={2} key={day}>
                  <Paper
                    sx={{
                      p: 1,
                      textAlign: "center",
                      background: status === "claimed"
                        ? "linear-gradient(135deg, #4caf50, #66bb6a)"
                        : status === "available"
                        ? `linear-gradient(135deg, ${reward.color}, ${reward.color}90)`
                        : "rgba(0, 0, 0, 0.4)",
                      color: "#fff",
                      borderRadius: 2,
                      position: "relative",
                      border: isSpecial ? "2px solid #ffeb3b" : "1px solid rgba(255,255,255,0.2)",
                      minHeight: isMobile ? "90px" : "80px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      cursor: status === "available" ? "pointer" : "default",
                      transform: status === "available" ? "scale(1.05)" : "scale(1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: status === "available" ? "scale(1.08)" : "scale(1.02)",
                      }
                    }}
                  >
                    {/* Special Badge */}
                    {isSpecial && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -8,
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: "linear-gradient(45deg, #ffeb3b, #ffc107)",
                          color: "#000",
                          px: 1,
                          py: 0.2,
                          borderRadius: 1,
                          fontSize: "10px",
                          fontWeight: "bold",
                        }}
                      >
                        {dayData?.occasion || t('monthlyPromotions.special')}
                      </Box>
                    )}

                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                      {t('monthlyPromotions.day')} {day}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body1" sx={{ fontSize: isMobile ? '14px' : '16px' }}>
                        {reward.icon}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          color: status === "available" ? "#fff" :
                                 status === "claimed" ? "#fff" : "#888",
                          fontSize: isMobile ? '9px' : '10px'
                        }}
                      >
                        {reward.text} {" "}{dayData?.rewardType=="GIFTCARD"
                        || dayData?.rewardType=="BOOSTER" || dayData?.rewardType=="FREE_SPIN" ? "" : (`(${dayData?.multiplier || 1}x)`)}
                      </Typography>
                    </Box>

                    {status === "available" && (
                      <Button
                        size="small"
                        disabled={IsLoginData?.dailyReward?.claimed && day === currentDay}
                        sx={{
                          mt: 0.5,
                          background: "#4caf50",
                          color: "black",
                          fontSize: isMobile ? "9px" : "10px",
                          minHeight: isMobile ? "18px" : "20px",
                          px: isMobile ? 0.5 : 1,
                          "&:hover": {
                            background: "#4caf50",
                          }
                        }}
                        onClick={() => { claimDailyReward(dayData); }}
                      >
                        {IsLoginData?.dailyReward?.claimed && day == currentDay
                        ? (isMobile ? t('monthlyPromotions.claimed') : t('monthlyPromotions.alreadyClaimed'))
                        : (dayData?.rewardType === "BOOSTER" ? t('monthlyPromotions.buyNow') : t('monthlyPromotions.claimNow'))}
                      </Button>
                    )}

                    {status === "claimed" && (
                      <Typography variant="body1" sx={{ color: "#fff" }}>
                        ✓
                      </Typography>
                    )}

                    {status === "locked" && (
                      <Typography variant="body1" sx={{ color: "#666" }}>
                        ✕
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                );
              })}
            </Grid>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Celebration overlay — shows when the streak is completed */}
      <StreakCelebration
        open={IsLoginData?.dailyReward?.streakCompleted === true}
        streakDays={currentDay || totalDays || 7}
        totalSc={IsLoginData?.dailyReward?.totalSc}
        totalGc={IsLoginData?.dailyReward?.totalGc}
        totalFreeSpin={IsLoginData?.dailyReward?.totalFreeSpin}
        starting={startingNew}
        onStartNew={startNewStreak}
        onClose={handleClose}
      />

      {/* At-risk overlay — shows when the streak is at risk */}
      <StreakAtRisk
        open={IsLoginData?.dailyReward?.streakRisk === true && !recoveryPackage}
        busy={riskBusy}
        price={recoveryInfo?.purchase_price}
        onBuy={buyToKeepStreak}
        onStartFresh={startFreshStreak}
        onLater={handleClose}
      />
    </Dialog>

    {/* Recovery package purchase (auto-recovers streak on success) */}
    {recoveryPackage && (
      <PaymentModal
        open={Boolean(recoveryPackage)}
        onClose={() => setRecoveryPackage(null)}
        selecteNewPackage={recoveryPackage}
        getCoinPackageList={() => {}}
        GetUserById={GetUserById}
        paymentMode={[]}
      />
    )}
    </>
    )
  );
}

export default MonthlyPromotions;
